/**
 * UploadFileDialog Component
 *
 * A dialog that allows users to upload files (e.g. PDFs) to an existing
 * journal article source. Uses the presigned URL upload flow from biblioService:
 *   1. User selects a file via drag-and-drop or file picker
 *   2. On submit, the file is uploaded to S3 via a presigned URL
 *   3. The S3 key is sent to the backend which transfers the file to Google Drive
 *
 * After a successful upload, the parent is notified via onUploadComplete so it
 * can refresh the files list.
 */

import { useState } from "react"
import { Upload, X, FileText, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { useFileUpload, FileWithPreview } from "@/hooks/use-file-upload"
import { addFilesToSource } from "@/services/biblioService"
import { useAuth } from "@/hooks/useAuth"


/**
 * Props for the UploadFileDialog component.
 *
 * @property sourceId - The numeric source ID of the journal article to upload files to.
 * @property onUploadComplete - Callback fired after a successful upload so the parent can refresh.
 */
interface UploadFileDialogProps {
  /** The numeric source ID of the journal article to upload files to. */
  sourceId: number
  /** Callback fired after a successful upload so the parent can refresh the files list. */
  onUploadComplete: () => void
}

/**
 * Dialog component for uploading files to a journal article source.
 *
 * Renders a trigger button (small upload icon) and a dialog with a drag-and-drop
 * file picker zone. On submission, orchestrates the presigned URL upload flow
 * via biblioService.addFilesToSource.
 *
 * @param props - Component props.
 * @param props.sourceId - The source ID to attach the uploaded file to.
 * @param props.onUploadComplete - Callback invoked on successful upload.
 * @returns The rendered dialog trigger and content.
 */
export default function UploadFileDialog({
  sourceId,
  onUploadComplete,
}: UploadFileDialogProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  const [open, setOpen] = useState<boolean>(false)
  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const [{ files, isDragging, errors: fileErrors }, {
    openFileDialog,
    removeFile,
    clearFiles,
    getInputProps,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  }] = useFileUpload({
    // Accept common document types — PDFs, Word docs, spreadsheets, images
    accept: ".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.tiff,.tif",
    multiple: true,
    maxSize: 100 * 1024 * 1024, // 100MB max per file
  })

  /**
   * Reset the dialog state when opening/closing.
   *
   * @param isOpen - Whether the dialog is being opened or closed.
   */
  const handleOpenChange = (isOpen: boolean): void => {
    setOpen(isOpen)
    if (!isOpen) {
      // Reset state on close
      clearFiles()
      setUploadError(null)
      setUploading(false)
    }
  }

  /**
   * Handle the upload submission.
   *
   * Orchestrates the full presigned URL flow:
   *   1. Gets presigned S3 PUT URLs for each file
   *   2. Uploads files directly to S3
   *   3. Sends S3 keys to backend to transfer to Google Drive
   *
   * On success, notifies the parent and closes the dialog.
   */
  const handleUpload = async (): Promise<void> => {
    if (files.length === 0 || uploading) return

    setUploading(true)
    setUploadError(null)

    try {
      const token = await getAccessToken()
      await addFilesToSource(
        token,
        sourceId.toString(),
        files,
      )

      // Success — notify parent and close
      onUploadComplete()
      handleOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed. Please try again."
      setUploadError(message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          aria-label="Upload file to this source"
        >
          <Upload className="size-3.5" />
          Upload
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload File</DialogTitle>
          <DialogDescription>
            Upload a file (e.g. full-text PDF) to source {sourceId}. The file
            will be stored in Google Drive.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            transition-colors
            ${isDragging
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50"
            }
          `}
          onClick={openFileDialog}
          role="button"
          tabIndex={0}
          aria-label="Click or drag files here to upload"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              openFileDialog()
            }
          }}
        >
          <input {...getInputProps()} />
          <Upload className="size-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {isDragging
              ? "Drop files here..."
              : "Click or drag files here to upload"
            }
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PDF, DOC, DOCX, XLS, XLSX, CSV, PNG, JPG, TIFF (max 100MB)
          </p>
        </div>

        {/* File errors from validation */}
        {fileErrors.length > 0 && (
          <div className="text-sm text-destructive space-y-1">
            {fileErrors.map((error, idx) => (
              <p key={idx}>{error}</p>
            ))}
          </div>
        )}

        {/* Selected files list */}
        {files.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {files.map((fileItem: FileWithPreview) => (
              <div
                key={fileItem.id}
                className="flex items-center gap-2 rounded border p-2 text-sm"
              >
                <FileText className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate flex-1 min-w-0">
                  {fileItem.file instanceof File
                    ? fileItem.file.name
                    : fileItem.file.name
                  }
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatFileSize(fileItem.file instanceof File ? fileItem.file.size : fileItem.file.size)}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeFile(fileItem.id)
                  }}
                  disabled={uploading}
                  aria-label={`Remove ${fileItem.file instanceof File ? fileItem.file.name : fileItem.file.name}`}
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Upload error message */}
        {uploadError && (
          <p className="text-sm text-destructive">{uploadError}</p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              `Upload ${files.length > 0 ? `(${files.length})` : ""}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


/**
 * Format a file size in bytes to a human-readable string.
 *
 * @param bytes - The file size in bytes.
 * @returns A formatted string (e.g. "1.5 MB", "320 KB").
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  return `${value.toFixed(exponent > 0 ? 1 : 0)} ${units[exponent]}`
}
